import type { AxiosResponse } from "axios";
import { GlobalAxios } from "../../../axios/axios-config";
import { IReport } from "../../../models/report";
import { ISearchReturn } from "../../../models/requests";
import MainApiService from "../../../services/mainApi.service";
import {
  getInternalReportsSearchResponse,
  isInternalReportType,
} from "./internalReports";
import { REPORT_TYPE_IDS } from "./reportTypes";

const REPORTS_VIEW_ROUTE = process.env.REACT_APP_MAIN + "/api/v1/Report";

export const REPORTS_TRANSLATION_PATH = "page.reports.";
export const REPORTS_MENU_TRANSLATION_PATH = "components.menu.reports.";
export const REPORTS_VIEW_MODE_TRANSLATION_PATH =
  "components.viewModeSelect.";
export const REPORTS_VIEW_MODE_STORAGE_KEY = "reportsViewMode";
export { REPORT_TYPE_IDS };

export type TReportsViewMode = "list" | "grid";

export const updateReportDescription = ({
  id,
  description,
}: {
  id: string;
  description: string | null;
}) =>
  GlobalAxios.put(`${REPORTS_VIEW_ROUTE}/UpdateDescription/${id}`, {
    id,
    description,
  });

export interface IReportsSearchParams {
  SearchBy?: string;
  PageNumber?: number;
  PageSize?: number;
  ReportTypeId: string;
}

const REPORTS_FRONTEND_SEARCH_PAGE_SIZE = 1000;

const apiReportsViewService = new MainApiService<IReport>(REPORTS_VIEW_ROUTE);
const getApiReports = apiReportsViewService.getAll;

const normalizeSearch = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase();

export const getReportCode = (report?: IReport | null) =>
  report?.code ?? report?.reportCode;

const filterReportsBySearch = (reports: IReport[], searchBy?: string) => {
  const normalizedSearch = normalizeSearch(searchBy?.trim() ?? "");

  if (!normalizedSearch) return reports;

  return reports.filter((report) => {
    const searchableText = [
      getReportCode(report),
      report.name,
      report.description,
      getReportTypeLabel(report),
    ]
      .filter((value) => value !== undefined && value !== null)
      .join(" ");

    return normalizeSearch(searchableText).includes(normalizedSearch);
  });
};

const paginateReports = (
  reports: IReport[],
  pageNumber: number,
  pageSize: number,
): ISearchReturn<IReport> => {
  const totalPages = Math.ceil(reports.length / pageSize);
  const start = (pageNumber - 1) * pageSize;
  const items = reports.slice(start, start + pageSize);

  return {
    items,
    pageNumber,
    totalPages,
    totalCount: reports.length,
    hasPreviousPage: pageNumber > 1,
    hasNextPage: pageNumber < totalPages,
  };
};

const getApiReportsSearchResponse = async (
  searchParams?: IReportsSearchParams,
): Promise<AxiosResponse<ISearchReturn<IReport>>> => {
  const pageNumber = searchParams?.PageNumber ?? 1;
  const pageSize = searchParams?.PageSize ?? 25;
  const searchBy = searchParams?.SearchBy;

  if (!searchBy?.trim()) {
    return getApiReports(searchParams);
  }

  const { data, ...response } = await getApiReports({
    ...searchParams,
    SearchBy: undefined,
    PageNumber: 1,
    PageSize: REPORTS_FRONTEND_SEARCH_PAGE_SIZE,
  });

  const filteredItems = filterReportsBySearch(data.items ?? [], searchBy);

  return {
    ...response,
    data: paginateReports(filteredItems, pageNumber, pageSize),
  } as AxiosResponse<ISearchReturn<IReport>>;
};

export const reportsViewService = Object.assign(apiReportsViewService, {
  getAll: (searchParams?: IReportsSearchParams) => {
    if (isInternalReportType(searchParams?.ReportTypeId)) {
      return getInternalReportsSearchResponse(searchParams);
    }

    return getApiReportsSearchResponse(searchParams);
  },
});

export const getReportTypeContextTranslationKey = (typeId?: string) => {
  switch (typeId) {
    case REPORT_TYPE_IDS.crm:
      return REPORTS_MENU_TRANSLATION_PATH + "crm";
    case REPORT_TYPE_IDS.internal:
      return REPORTS_MENU_TRANSLATION_PATH + "internal";
    default:
      return undefined;
  }
};

export const getReportExecutionRoute = (typeId: string, reportId: string) => {
  const routeTypeId = isInternalReportType(typeId)
    ? REPORT_TYPE_IDS.internal
    : typeId;

  return `../${routeTypeId}/report/${reportId}`;
};

const isReportTypeOption = (
  reportType: IReport["reportType"] | IReport["type"],
): reportType is NonNullable<IReport["reportType"]> =>
  !!reportType && typeof reportType !== "string";

export const getReportTypeLabel = (report: IReport) => {
  const reportType = report.reportType ?? report.type;

  if (typeof reportType === "string" && reportType.length > 0) {
    return reportType;
  }

  return (
    (isReportTypeOption(reportType) ? reportType.name : undefined) ??
    report.reportTypeName ??
    (isReportTypeOption(reportType) ? reportType.description : undefined) ??
    report.reportTypeId ??
    "-"
  );
};
